// Comment suppression while a demonstration timing rail is in edit mode:
// Feature MCRO001-F0060 (parented to F0028), Test T-MCRO-060 — owner ruling
// 2026-08-26, verbatim: "flow should be move mouse over rails, rails expand
// to make it easier to edit and the commenter box is disabled. I edit bars
// and then move away from rails, rails fold back into the margin and then
// comment box enables." docs/process/tests/T-MCRO-060-rail-gutter-hover-edit.md,
// choreo MR !148 amends MCRO001-D006 §4.2. Tracking: studioaeye-WebUI #42.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { choreographyApi } from '../../services/api';

const responsePayload = (response) => response?.data?.data || response?.data || {};
const errorMessage = (error) => {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.response?.data?.code || error?.response?.data?.error?.code || error?.response?.data?.error || '');
  if ([401, 403, 404].includes(status) || /BLOCK|SUSPEND|REVOK|RESOURCE_ACCESS/i.test(code)) {
    return 'This review is not available for this account. Contact the Sheet owner if you think this is a mistake.';
  }
  return error?.response?.data?.message || error?.response?.data?.error?.message || error?.message || 'Community review is unavailable.';
};

export const COMMUNITY_MODES = [
  { id: 'edit', label: 'Edit', description: 'Edit a permitted draft and use review tools.' },
  { id: 'comment', label: 'Comment + tag media', description: 'Keep choreography immutable while adding review contributions.' },
  { id: 'view', label: 'View', description: 'Read and rehearse without writing.' },
];

export const COMMUNITY_REVIEW_DISPLAYS = [
  { id: 'inline', label: 'Inline' },
  { id: 'list', label: 'List' },
  { id: 'hide', label: 'Hide' },
];

export function normalizeCommunityReviewDisplay(value) {
  return COMMUNITY_REVIEW_DISPLAYS.some(({ id }) => id === value) ? value : 'inline';
}

export const communityReviewDisplayKey = (userId, orgId, routineId) => userId && orgId && routineId
  ? `studioaeye:choreography:review-view:v1:${userId}:${orgId}:${routineId}`
  : null;

export function readCommunityReviewDisplay(userId, orgId, routineId, storage) {
  const key = communityReviewDisplayKey(userId, orgId, routineId);
  if (!key) return 'inline';
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    const value = target?.getItem(key);
    return normalizeCommunityReviewDisplay(value);
  } catch { return 'inline'; }
}

export function writeCommunityReviewDisplay(userId, orgId, routineId, value, storage) {
  const key = communityReviewDisplayKey(userId, orgId, routineId);
  if (!key || normalizeCommunityReviewDisplay(value) !== value) return false;
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    target?.setItem(key, value);
    return true;
  } catch { return false; }
}

export const COMMUNITY_TONES = [
  { id: 'serious', label: 'Serious', icon: '◆' },
  { id: 'playful', label: 'Playful', icon: '✦' },
];

export const COMMUNITY_VISUAL_TREATMENTS = [
  { id: 'professional', label: 'Professional', description: 'Quiet editorial presentation' },
  { id: 'social_fun', label: 'Fun / social', description: 'Bright, expressive presentation' },
];

export const COMMUNITY_BORDER_STYLES = [
  { id: 'clean', label: 'Clean line' },
  { id: 'bold', label: 'Bold ribbon' },
  { id: 'outlandish', label: 'Outlandish starburst' },
  { id: 'blooms_string', label: 'Blooms on a string' },
];

const visualTreatment = (value) => COMMUNITY_VISUAL_TREATMENTS.some(({ id }) => id === value) ? value : 'professional';
const borderStyle = (value) => COMMUNITY_BORDER_STYLES.some(({ id }) => id === value) ? value : 'clean';

export const FRAME_SHAPE_TOOLS = [
  { id: 'rectangle', label: 'Rectangle', geometry: { points: [{ x: 0.2, y: 0.2 }, { x: 0.6, y: 0.5 }] } },
  { id: 'arrow', label: 'Arrow', geometry: { points: [{ x: 0.2, y: 0.5 }, { x: 0.7, y: 0.5 }] } },
  { id: 'freehand', label: 'Freehand line', geometry: { points: [{ x: 0.2, y: 0.5 }, { x: 0.4, y: 0.3 }, { x: 0.7, y: 0.6 }] } },
];

const permission = (authority, capabilities, authorityKey, capabilityKey) => authority?.[authorityKey] === true
  || (authority?.[authorityKey] !== false && capabilities?.[capabilityKey] === true);

export function effectiveCommunityAuthority(capabilities = {}, policy = {}, authority = {}, anonymous = false) {
  const canView = authority.canView !== false && capabilities.choreography_view !== false;
  const contributionsEnabled = policy.contributionsEnabled !== false && policy.mode !== 'view' && !anonymous;
  return {
    canView,
    canEdit: !anonymous && policy.mode === 'edit' && permission(authority, capabilities, 'canEdit', 'choreography_edit_all'),
    canContribute: contributionsEnabled && permission(authority, capabilities, 'canContribute', 'choreography_contribute'),
    canUploadMedia: contributionsEnabled && permission(authority, capabilities, 'canUploadMedia', 'choreography_contribution_media_upload_own'),
    canAnnotateOwnMedia: contributionsEnabled && permission(authority, capabilities, 'canAnnotateOwnMedia', 'choreography_contribution_annotate_own'),
    canModerate: !anonymous && permission(authority, capabilities, 'canModerate', 'choreography_contributions_moderate'),
  };
}

// MCRO001-D003 §4/§5 owner-approved amendment: a comment binds to exactly one
// evidence kind — a published revisionId, or a draft's draftId +
// draftServerSequence. `binding` accepts either a plain revisionId string
// (published, preserving every existing call site) or
// `{ draftId, draftServerSequence }` for a draft-anchored capture.
export function anchorFromSelection(source, start, end, binding, sourceHash) {
  const safeSource = String(source || '');
  const from = Math.max(0, Math.min(Number(start) || 0, safeSource.length));
  const to = Math.max(from, Math.min(Number(end) || from, safeSource.length));
  const selected = safeSource.slice(from, to);
  const lineStart = selected ? from : safeSource.lastIndexOf('\n', from - 1) + 1;
  const nextLineBreak = selected ? -1 : safeSource.indexOf('\n', from);
  const lineEnd = selected ? to : (nextLineBreak === -1 ? safeSource.length : nextLineBreak);
  const sourceStart = selected ? from : lineStart;
  const sourceEnd = selected ? to : lineEnd;
  const exactQuote = selected || safeSource.slice(lineStart, lineEnd);
  const startLine = safeSource.slice(0, sourceStart).split('\n').length;
  const isDraftBinding = binding && typeof binding === 'object';
  return {
    ...(isDraftBinding
      ? { draftId: binding.draftId, draftServerSequence: binding.draftServerSequence }
      : { revisionId: binding }),
    sourceHash,
    type: selected ? 'text' : 'line',
    sourceStart,
    sourceEnd,
    exactQuote,
    prefix: safeSource.slice(Math.max(0, sourceStart - 24), sourceStart),
    suffix: safeSource.slice(sourceEnd, sourceEnd + 24),
    sourceLine: startLine,
    state: 'attached',
    version: 1,
  };
}

function lineForOffset(source, offset) {
  return String(source || '').slice(0, Math.max(0, Number(offset) || 0)).split('\n').length;
}

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
const inlineBubbleShift = (anchorX, viewportWidth) => {
  const bubbleWidth = Math.min(352, Math.max(0, viewportWidth - 16));
  const desiredLeft = clamp(anchorX - 16, 8, viewportWidth - bubbleWidth - 8);
  return `${Math.round(desiredLeft - anchorX)}px`;
};

export function selectionToolbarPosition(target, source, offset) {
  const rect = target?.getBoundingClientRect?.() || { top: 0, left: 0, right: 320, bottom: 240, width: 320 };
  let styles = {};
  try { styles = globalThis.getComputedStyle?.(target) || {}; } catch { styles = {}; }
  const fontSize = Number.parseFloat(styles.fontSize) || 16;
  const lineHeight = Number.parseFloat(styles.lineHeight) || fontSize * 1.6;
  const paddingTop = Number.parseFloat(styles.paddingTop) || 12;
  const paddingLeft = Number.parseFloat(styles.paddingLeft) || 12;
  const before = String(source || '').slice(0, Math.max(0, Number(offset) || 0));
  const lines = before.split('\n');
  const lineIndex = lines.length - 1;
  const column = lines[lineIndex]?.length || 0;
  const viewportWidth = Number(globalThis.innerWidth) || 1024;
  const viewportHeight = Number(globalThis.innerHeight) || 768;
  const estimatedX = Number(rect.left || 0) + paddingLeft + (column * fontSize * 0.58) - Number(target?.scrollLeft || 0);
  const estimatedY = Number(rect.top || 0) + paddingTop + ((lineIndex + 1) * lineHeight) - Number(target?.scrollTop || 0) + 8;
  return {
    left: `${Math.round(clamp(estimatedX, 12, viewportWidth - 236))}px`,
    top: `${Math.round(clamp(estimatedY, 12, viewportHeight - 76))}px`,
  };
}

export function inlineAnchorPosition(target, source, offset) {
  const rect = target?.getBoundingClientRect?.() || { top: 0, left: 0, right: 640, bottom: 480, width: 640 };
  const viewportWidth = Number(globalThis.innerWidth) || 1024;
  const viewportHeight = Number(globalThis.innerHeight) || 768;
  const before = String(source || '').slice(0, Math.max(0, Number(offset) || 0));
  const lines = before.split('\n');
  const lineIndex = lines.length - 1;
  const column = lines[lineIndex]?.length || 0;
  const mirrorLine = target?.closest?.('.mc-continuous-canvas')?.querySelector?.(`.mc-continuous-canvas-mirror .mc-live-line[data-source-line="${lineIndex + 1}"] .mc-live-line-content`);
  if (mirrorLine && globalThis.document?.createRange) {
    const walker = globalThis.document.createTreeWalker(mirrorLine, globalThis.NodeFilter?.SHOW_TEXT || 4);
    let remaining = column;
    let node = walker.nextNode();
    while (node && remaining > node.textContent.length) {
      remaining -= node.textContent.length;
      node = walker.nextNode();
    }
    if (node) {
      const range = globalThis.document.createRange();
      range.setStart(node, Math.min(remaining, node.textContent.length));
      range.collapse(true);
      const exact = range.getBoundingClientRect?.();
      if (exact && [exact.left, exact.top].every(Number.isFinite) && (exact.left !== 0 || exact.top !== 0 || exact.height > 0)) {
        const offscreen = exact.top < 0 || exact.top > viewportHeight || exact.left < Math.max(0, Number(rect.left || 0)) || exact.left > Math.min(viewportWidth, Number(rect.right || viewportWidth));
        const above = exact.top > viewportHeight - 300;
        return {
          left: `${Math.round(clamp(exact.left, 16, viewportWidth - 16))}px`,
          top: `${Math.round(clamp(exact.top, 16, viewportHeight - 16))}px`,
          display: offscreen ? 'none' : undefined,
          '--mc-inline-bubble-shift': inlineBubbleShift(clamp(exact.left, 16, viewportWidth - 16), viewportWidth),
          '--mc-inline-bubble-top': above ? 'auto' : '.15rem',
          '--mc-inline-bubble-bottom': above ? '1.65rem' : 'auto',
        };
      }
    }
  }
  let styles = {};
  try { styles = globalThis.getComputedStyle?.(target) || {}; } catch { styles = {}; }
  const fontSize = Number.parseFloat(styles.fontSize) || 16;
  const lineHeight = Number.parseFloat(styles.lineHeight) || fontSize * 1.85;
  const paddingTop = Number.parseFloat(styles.paddingTop) || 24;
  const paddingLeft = Number.parseFloat(styles.paddingLeft) || 104;
  const estimatedX = Number(rect.left || 0) + paddingLeft + (column * fontSize * 0.58) - Number(target?.scrollLeft || 0);
  const estimatedY = Number(rect.top || 0) + paddingTop + (lineIndex * lineHeight) - Number(target?.scrollTop || 0);
  const clampedX = clamp(estimatedX, 16, viewportWidth - 16);
  const sourceRight = Number(rect.right) > Number(rect.left) ? Number(rect.right) : viewportWidth;
  const sourceBottom = Number(rect.bottom) > Number(rect.top) ? Number(rect.bottom) : viewportHeight;
  const offscreen = estimatedY < Math.max(0, Number(rect.top || 0)) || estimatedY > Math.min(viewportHeight, sourceBottom)
    || estimatedX < Math.max(0, Number(rect.left || 0)) || estimatedX > Math.min(viewportWidth, sourceRight);
  return {
    left: `${Math.round(clampedX)}px`,
    top: `${Math.round(clamp(estimatedY, 16, viewportHeight - 16))}px`,
    display: offscreen ? 'none' : undefined,
    '--mc-inline-bubble-shift': inlineBubbleShift(clampedX, viewportWidth),
    '--mc-inline-bubble-top': estimatedY > viewportHeight - 300 ? 'auto' : '.15rem',
    '--mc-inline-bubble-bottom': estimatedY > viewportHeight - 300 ? '1.65rem' : 'auto',
  };
}

// D003 §4: the client request UUID is generated once per composer attempt and
// scoped server-side to (organisation, routine, stable author subject, UUID).
const newClientRequestId = () => globalThis.crypto?.randomUUID?.() || null;

// D001 §5.10: on narrow screens floating surfaces become non-modal bottom
// sheets instead of hovering beside the caret.
export function useNarrowSelectionSheet() {
  const query = '(max-width: 720px)';
  const [sheet, setSheet] = useState(() => Boolean(globalThis.matchMedia?.(query)?.matches));
  useEffect(() => {
    const media = globalThis.matchMedia?.(query);
    if (!media?.addEventListener) return undefined;
    const update = () => setSheet(Boolean(media.matches));
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return sheet;
}

// The one contextual selection toolbar shared by the community review layer
// (D003 §4 Add comment) and the canvas structural actions (D001 §5.1). It
// hovers beside the selection — never over it — and becomes a bottom sheet on
// narrow screens so it cannot cover the keyboard or the current paragraph.
export function SelectionToolbar({ toolbarRef, position, quote, onDismiss, children }) {
  const sheet = useNarrowSelectionSheet();
  return <div
    ref={toolbarRef}
    className={`mc-selection-toolbar${sheet ? ' mc-selection-sheet' : ''}`}
    role="toolbar"
    aria-label="Selected choreography actions"
    style={sheet ? undefined : position}
    onPointerDown={(event) => event.preventDefault()}
  >
    <span className="mc-selection-quote" title={quote}>“{quote}”</span>
    {children}
    <button type="button" className="mc-selection-dismiss" aria-label="Dismiss selected choreography actions" onClick={onDismiss}>×</button>
  </div>;
}

// D003 §4: floating composer/bubble surfaces have a labelled drag handle,
// pointer/touch repositioning and an equivalent keyboard path (arrows move
// 8 CSS pixels, Shift+arrows 32, Escape restores the pre-move position and
// Reset position returns to automatic placement with focus on the handle).
// Position lives only in component memory keyed by the surface and is
// discarded on unmount/cancel/success/display change. It never changes the
// ReviewAnchor or any stored record and stays clamped to the viewport.
export function FloatingReviewSurface({ surfaceKey, label, className = '', style, children }) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const surfaceRef = useRef(null);
  const handleRef = useRef(null);
  const preMoveRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  useEffect(() => { setOffset({ x: 0, y: 0 }); preMoveRef.current = { x: 0, y: 0 }; }, [surfaceKey]);
  const clampOffset = (candidate) => {
    const element = surfaceRef.current;
    if (!element?.getBoundingClientRect) return candidate;
    const rect = element.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) return candidate;
    const viewportWidth = Number(globalThis.innerWidth) || 1024;
    const viewportHeight = Number(globalThis.innerHeight) || 768;
    const baseLeft = rect.left - offset.x;
    const baseTop = rect.top - offset.y;
    return {
      x: clamp(candidate.x, 8 - baseLeft, Math.max(8 - baseLeft, viewportWidth - rect.width - 8 - baseLeft)),
      y: clamp(candidate.y, 8 - baseTop, Math.max(8 - baseTop, viewportHeight - rect.height - 8 - baseTop)),
    };
  };
  const move = (dx, dy) => setOffset((current) => clampOffset({ x: current.x + dx, y: current.y + dy }));
  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, base: offset };
    preMoveRef.current = offset;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };
  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset(clampOffset({ x: drag.base.x + (event.clientX - drag.x), y: drag.base.y + (event.clientY - drag.y) }));
  };
  const releasePointer = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
  };
  useEffect(() => () => { dragRef.current = null; }, []);
  const handleKeyDown = (event) => {
    const step = event.shiftKey ? 32 : 8;
    if (event.key === 'ArrowLeft') { event.preventDefault(); move(-step, 0); }
    else if (event.key === 'ArrowRight') { event.preventDefault(); move(step, 0); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); move(0, -step); }
    else if (event.key === 'ArrowDown') { event.preventDefault(); move(0, step); }
    else if (event.key === 'Escape' && (offset.x !== preMoveRef.current.x || offset.y !== preMoveRef.current.y)) {
      event.preventDefault();
      event.stopPropagation();
      setOffset(preMoveRef.current);
    }
  };
  return <div
    ref={surfaceRef}
    className={`mc-floating-surface ${className}`.trim()}
    style={{ ...style, transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined }}
  >
    <div className="mc-floating-surface-bar">
      <button
        ref={handleRef}
        type="button"
        className="mc-floating-drag-handle"
        aria-label={`Move ${label}`}
        title={`Move ${label}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
        onFocus={() => { if (!dragRef.current) preMoveRef.current = offset; }}
        onKeyDown={handleKeyDown}
      ><span aria-hidden="true">⠿</span><span className="mc-visually-hidden">Move {label}. Arrow keys move 8 pixels, Shift with arrows moves 32, Escape restores the position.</span></button>
      {(offset.x !== 0 || offset.y !== 0) && <button type="button" className="mc-floating-reset" onClick={() => { setOffset({ x: 0, y: 0 }); preMoveRef.current = { x: 0, y: 0 }; handleRef.current?.focus(); }}>Reset position</button>}
    </div>
    {children}
  </div>;
}

export function moveAnchorToAdjacentLine(source, anchor = {}, delta = 0) {
  const safeSource = String(source || '');
  const lines = safeSource.split('\n');
  const currentLine = lineForOffset(safeSource, anchor.sourceStart) - 1;
  const targetLine = Math.max(0, Math.min(lines.length - 1, currentLine + Number(delta || 0)));
  const sourceStart = lines.slice(0, targetLine).reduce((offset, line) => offset + line.length + 1, 0);
  const exactQuote = lines[targetLine] || '';
  const sourceEnd = sourceStart + exactQuote.length;
  return {
    ...anchor,
    type: 'line',
    sourceStart,
    sourceEnd,
    exactQuote,
    prefix: safeSource.slice(Math.max(0, sourceStart - 24), sourceStart),
    suffix: safeSource.slice(sourceEnd, sourceEnd + 24),
    sourceLine: targetLine + 1,
    state: 'attached',
  };
}

export function normalizeCommunity(value = {}) {
  const media = value.media || [];
  const shapes = value.shapes || [];
  const replies = value.comments || value.replies || [];
  const reactions = value.reactions || [];
  const threads = (value.threads || []).map((thread) => {
    const threadMedia = media.find((item) => item.threadId === thread.threadId || item.contributionId === thread.contributionId);
    const embeddedMedia = Array.isArray(thread.media) ? thread.media[0] : thread.media;
    const selectedMedia = threadMedia || embeddedMedia;
    const mediaWithShapes = selectedMedia ? { ...selectedMedia, shapes: selectedMedia.shapes || shapes.filter((shape) => shape.mediaId === selectedMedia.mediaId) } : null;
    const reactionCounts = reactions.filter((reaction) => reaction.threadId === thread.threadId).reduce((counts, reaction) => ({ ...counts, [reaction.reaction]: (counts[reaction.reaction] || 0) + 1 }), thread.reactionCounts || {});
    return {
      ...thread,
      body: thread.body || thread.comment?.body || '',
      replies: thread.replies || replies.filter((reply) => reply.threadId === thread.threadId && reply.replyId),
      media: mediaWithShapes,
      reactionCounts,
    };
  });
  return { ...value, threads };
}

export function packReviewBalloons(threads = [], railHeight = 720, cardHeight = 154, gap = 8) {
  const ordered = [...threads].sort((a, b) => Number(a.anchor?.sourceLine || 0) - Number(b.anchor?.sourceLine || 0) || String(a.threadId).localeCompare(String(b.threadId)));
  const capacity = Math.max(1, Math.floor((railHeight + gap) / (cardHeight + gap)));
  if (ordered.length > capacity) {
    const visible = ordered.slice(0, Math.max(0, capacity - 1));
    const clustered = ordered.slice(visible.length);
    return [...visible.map((thread, index) => ({ ...thread, packedTop: index * (cardHeight + gap) })), {
      threadId: `cluster-${clustered.map(({ threadId }) => threadId).join('-')}`,
      cluster: clustered,
      packedTop: visible.length * (cardHeight + gap),
    }];
  }
  let previousBottom = -gap;
  return ordered.map((thread) => {
    const desired = Math.max(0, (Number(thread.anchor?.sourceLine || 1) - 1) * 34);
    const packedTop = Math.max(desired, previousBottom + gap);
    previousBottom = packedTop + cardHeight;
    return { ...thread, packedTop };
  });
}

function OwnedContributionControls({ item, type, id, value, label, changes, onRefresh }) {
  const version = Number(item?.version);
  const allowed = item?.ownedByViewer === true && Number.isInteger(version) && version > 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [notice, setNotice] = useState('');
  useEffect(() => { if (!editing) setDraft(value || ''); }, [editing, value, version]);
  if (!allowed || !id) return null;

  const save = async (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    setNotice('');
    try {
      await choreographyApi.updateOwnedReviewContribution(type, id, { expectedVersion: version, changes: changes(draft.trim()) });
      setEditing(false);
      await onRefresh();
    } catch { setNotice(`Could not update this ${label.toLowerCase()}. Reload and try again.`); }
  };
  const withdraw = async () => {
    if (!globalThis.confirm?.(`Withdraw this ${label.toLowerCase()}? Its audit history will be preserved.`)) return;
    setNotice('');
    try {
      await choreographyApi.withdrawOwnedReviewContribution(type, id, { expectedVersion: version, reason: 'Withdrawn by author' });
      await onRefresh();
    } catch { setNotice(`Could not withdraw this ${label.toLowerCase()}. Reload and try again.`); }
  };

  return <div className="mc-owned-contribution">
    {editing ? <form aria-label={`Edit my ${label.toLowerCase()}`} onSubmit={save}><label>{label}<input aria-label={`${label} text`} value={draft} onChange={(event) => setDraft(event.target.value)} /></label><div><button type="button" onClick={() => setEditing(false)}>Cancel</button><button type="submit" disabled={!draft.trim()}>Save</button></div></form>
      : <div><button type="button" onClick={() => setEditing(true)}>Edit my {label.toLowerCase()}</button><button type="button" onClick={withdraw}>Withdraw my {label.toLowerCase()}</button></div>}
    {notice && <p role="alert">{notice}</p>}
  </div>;
}

function ThreadActions({ actions, authorName }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (open) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!menuRef.current?.contains(event.target) && event.target !== triggerRef.current) setOpen(false);
    };
    globalThis.document?.addEventListener('pointerdown', dismiss);
    return () => globalThis.document?.removeEventListener('pointerdown', dismiss);
  }, [open]);
  if (actions.length === 0) return null;
  const keyDown = (event) => {
    const items = [...(menuRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    const index = items.indexOf(globalThis.document?.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  };
  const runAction = async (action) => {
    setOpen(false);
    try {
      await action.run();
    } finally {
      globalThis.setTimeout?.(() => triggerRef.current?.focus(), 0);
    }
  };
  return <div className="mc-review-actions">
    <button ref={triggerRef} type="button" className="mc-review-actions-trigger" aria-label={`More actions for ${authorName ? `comment by ${authorName}` : 'comment'}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}><span aria-hidden="true">⌄</span></button>
    {open && <div ref={menuRef} className="mc-review-actions-menu" role="menu" aria-label={`Actions for ${authorName ? `comment by ${authorName}` : 'comment'}`} onKeyDown={keyDown}>{actions.map((action) => <button type="button" role="menuitem" key={action.label} onClick={() => runAction(action)}>{action.label}</button>)}</div>}
  </div>;
}

function ContributionFormatter({ target, formatting, onApply }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const paletteRef = useRef(null);
  const paletteId = `mc-format-palette-${String(target?.type || 'comment')}-${String(target?.id || 'contribution').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  useEffect(() => {
    setOpen(false);
  }, [target?.id, target?.type]);
  useEffect(() => {
    if (open) (paletteRef.current?.querySelector('[aria-pressed="true"]') || paletteRef.current?.querySelector('button'))?.focus();
  }, [open]);
  if (!target) return null;
  const moderatorReset = target.mode === 'moderator';
  const choices = moderatorReset ? COMMUNITY_BORDER_STYLES.filter(({ id }) => id === 'clean') : COMMUNITY_BORDER_STYLES;
  const formatterLabel = moderatorReset ? `Reset ${target.label} frame` : `Format ${target.label}`;
  const close = () => {
    setOpen(false);
    globalThis.setTimeout?.(() => triggerRef.current?.focus(), 0);
  };
  const keyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    const choices = [...(paletteRef.current?.querySelectorAll('button') || [])];
    if (choices.length === 0) return;
    event.preventDefault();
    const index = choices.indexOf(globalThis.document?.activeElement);
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? choices.length - 1
        : ['ArrowRight', 'ArrowDown'].includes(event.key) ? (index + 1) % choices.length
          : (index - 1 + choices.length) % choices.length;
    choices[next]?.focus();
  };
  return <div className={`mc-contribution-formatter${moderatorReset ? ' moderator-reset' : ''}`} aria-label={formatterLabel}>
    <button
      ref={triggerRef}
      type="button"
      className="mc-format-trigger"
      aria-label={moderatorReset ? `Reset this ${target.label} frame` : `Choose a picture frame for this ${target.label}`}
      aria-expanded={open}
      aria-controls={paletteId}
      title={formatterLabel}
      onClick={() => setOpen((value) => !value)}
    ><span aria-hidden="true">▣</span></button>
    {open && <div
      ref={paletteRef}
      id={paletteId}
      className="mc-format-palette"
      role="group"
      aria-label={moderatorReset ? `Reset border for this ${target.label}` : `Border choices for this ${target.label}`}
      onKeyDown={keyDown}
    >
      <span className="mc-format-target">{target.label}</span>
      {choices.map((choice) => <button
        type="button"
        key={choice.id}
        className={`border-${choice.id}`}
        aria-pressed={formatting.borderStyle === choice.id}
        disabled={formatting.pending}
        onClick={() => onApply(choice.id)}
      >{choice.label}</button>)}
    </div>}
  </div>;
}

function ThreadCard({ thread, authority, orgId, onRefresh, source, readOnly = false, active = false, inline = false, onRevealAnchor }) {
  const [reply, setReply] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [shapeLabel, setShapeLabel] = useState('');
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(thread.body || '');
  const [mutationNotice, setMutationNotice] = useState('');
  const media = thread.media || thread.attachments?.[0];
  const publicContentUrl = readOnly && typeof media?.contentUrl === 'string' && media.contentUrl.startsWith('/api/v1/public/choreography/')
    ? media.contentUrl
    : null;
  const photoUrl = readOnly ? publicContentUrl : (media?.thumbnailUrl || (media?.kind === 'photo' ? media?.contentUrl : null));
  const [commentFormatting, setCommentFormatting] = useState(() => ({
    visualTreatment: visualTreatment(thread.visualTreatment),
    borderStyle: borderStyle(thread.borderStyle),
    version: Number(thread.version),
    pending: false,
  }));
  const [mediaFormatting, setMediaFormatting] = useState(() => ({
    visualTreatment: visualTreatment(media?.visualTreatment),
    borderStyle: borderStyle(media?.borderStyle),
    version: Number(media?.version),
    pending: false,
  }));
  const [formatTarget, setFormatTarget] = useState('comment');
  const [tryThisFlourish, setTryThisFlourish] = useState(false);
  const tryThisTimerRef = useRef(null);
  const reducedMotion = Boolean(globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  const authorName = thread.author?.displayName || thread.authorName || '';
  const react = async (reaction) => {
    setMutationNotice('');
    try {
      await choreographyApi.addCommunityReaction(orgId, thread.threadId, reaction);
    } catch {
      setMutationNotice('Could not add that reaction. Reload and try again.');
      return;
    }
    if (reaction === 'try_this') {
      if (tryThisTimerRef.current) globalThis.clearTimeout?.(tryThisTimerRef.current);
      setTryThisFlourish(true);
      tryThisTimerRef.current = globalThis.setTimeout?.(() => {
        tryThisTimerRef.current = null;
        setTryThisFlourish(false);
      }, 5000);
    }
    try { await onRefresh(); }
    catch { setMutationNotice('Reaction added. Reload to refresh reaction counts.'); }
  };
  const changeState = async () => {
    setMutationNotice('');
    try { await choreographyApi.setCommunityThreadState(orgId, thread.threadId, { state: thread.state === 'resolved' ? 'open' : 'resolved' }); await onRefresh(); }
    catch { setMutationNotice('Could not update this note. Reload and try again.'); }
  };
  const postReply = async (event) => { event.preventDefault(); if (!reply.trim()) return; await choreographyApi.replyToCommunityThread(orgId, thread.threadId, { body: reply.trim(), tone: thread.tone || 'serious' }); setReply(''); onRefresh(); };
  const addShape = async (tool) => { if (!media?.mediaId || !shapeLabel.trim()) return; await choreographyApi.addCommunityMediaShape(orgId, media.mediaId, { kind: tool.id, label: shapeLabel.trim(), geometry: tool.geometry }); setShapeLabel(''); onRefresh(); };
  const moderate = async (decision) => {
    setMutationNotice('');
    try { await choreographyApi.moderateCommunityContribution(orgId, thread.contributionId || thread.threadId, { decision, reason: 'Community review moderation', type: thread.contributionType || 'comment' }); await onRefresh(); }
    catch { setMutationNotice('Could not apply that moderation decision. Reload and try again.'); }
  };
  const contributionId = thread.contributionId;
  const contributionVersion = Number(thread.version);
  const canManageOwnContribution = !readOnly && authority.canContribute && thread.ownedByViewer === true
    && Boolean(contributionId) && Number.isInteger(contributionVersion) && contributionVersion > 0;
  const canResetCommentPresentation = !readOnly && authority.canModerate && !canManageOwnContribution
    && Boolean(contributionId) && Number.isInteger(contributionVersion) && contributionVersion > 0;
  const mediaVersion = Number(media?.version);
  const canFormatMedia = !readOnly && authority.canContribute && media?.ownedByViewer === true
    && Boolean(media?.mediaId) && Number.isInteger(mediaVersion) && mediaVersion > 0;
  const canResetMediaPresentation = !readOnly && authority.canModerate && !canFormatMedia
    && Boolean(media?.mediaId) && Number.isInteger(mediaVersion) && mediaVersion > 0;
  const canSelectCommentPresentation = canManageOwnContribution || canResetCommentPresentation;
  const canSelectMediaPresentation = canFormatMedia || canResetMediaPresentation;
  const canManageThreadState = canManageOwnContribution || authority.canModerate;
  const anchorVersion = Number(thread.anchor?.version);
  const canMoveOwnAnchor = canManageOwnContribution && Number.isInteger(anchorVersion) && anchorVersion > 0;
  const canAnnotateMedia = authority.canAnnotateOwnMedia && media?.ownedByViewer === true;
  const saveOwnContribution = async (event) => {
    event.preventDefault();
    if (!editedBody.trim() || !canManageOwnContribution) return;
    setMutationNotice('');
    try {
      await choreographyApi.updateOwnedReviewContribution('comment', contributionId, {
        expectedVersion: contributionVersion,
        changes: { body: editedBody.trim(), tone: thread.tone || 'serious' },
      });
      setEditing(false);
      await onRefresh();
    } catch { setMutationNotice('Could not update your review. Reload and try again.'); }
  };
  const withdrawOwnContribution = async () => {
    if (!canManageOwnContribution || !globalThis.confirm?.('Withdraw this review contribution? Its audit history will be preserved.')) return;
    setMutationNotice('');
    try {
      await choreographyApi.withdrawOwnedReviewContribution('comment', contributionId, {
        expectedVersion: contributionVersion,
        reason: 'Withdrawn by author',
      });
      await onRefresh();
    } catch (error) {
      // !434 / MCRO001-F0018: a stale-version 409 reloads the latest owner
      // projection, and the notice says only what actually happened — never
      // that the latest version loaded when the reload itself failed.
      if (error?.response?.status === 409) {
        let reloaded = false;
        try { reloaded = (await onRefresh()) !== false; } catch { reloaded = false; }
        setMutationNotice(reloaded
          ? 'Your review changed elsewhere, so it was not deleted. The latest version has been loaded; review it before trying again.'
          : 'Your review changed elsewhere, so it was not deleted. The latest version could not be loaded. Reload and try again.');
      } else {
        setMutationNotice('Could not withdraw your review. Reload and try again.');
      }
    }
  };
  useEffect(() => { if (!editing) setEditedBody(thread.body || ''); }, [editing, thread.body, thread.version]);
  useEffect(() => () => {
    if (tryThisTimerRef.current) globalThis.clearTimeout?.(tryThisTimerRef.current);
  }, []);
  useEffect(() => {
    setCommentFormatting({
      visualTreatment: visualTreatment(thread.visualTreatment),
      borderStyle: borderStyle(thread.borderStyle),
      version: contributionVersion,
      pending: false,
    });
  }, [contributionId, contributionVersion]);
  useEffect(() => {
    setMediaFormatting({
      visualTreatment: visualTreatment(media?.visualTreatment),
      borderStyle: borderStyle(media?.borderStyle),
      version: mediaVersion,
      pending: false,
    });
  }, [media?.mediaId, mediaVersion]);
  useEffect(() => {
    if (formatTarget === 'comment' && !canSelectCommentPresentation && canSelectMediaPresentation) setFormatTarget('media');
    if (formatTarget === 'media' && !canSelectMediaPresentation && canSelectCommentPresentation) setFormatTarget('comment');
  }, [canSelectCommentPresentation, canSelectMediaPresentation, formatTarget]);
  const applyBorder = async (nextBorder) => {
    const normalizedBorder = borderStyle(nextBorder);
    const nextVisual = normalizedBorder === 'clean' ? 'professional' : 'social_fun';
    const isMedia = formatTarget === 'media';
    const moderatorReset = isMedia ? canResetMediaPresentation : canResetCommentPresentation;
    const currentFormatting = isMedia ? mediaFormatting : commentFormatting;
    const target = isMedia
      ? { type: 'media', id: media?.mediaId, version: currentFormatting.version, allowed: canFormatMedia || canResetMediaPresentation }
      : { type: 'comment', id: contributionId, version: currentFormatting.version, allowed: canManageOwnContribution || canResetCommentPresentation };
    if (!target.allowed || !target.id) return;
    const setFormatting = isMedia ? setMediaFormatting : setCommentFormatting;
    const previous = currentFormatting;
    setMutationNotice('');
    setFormatting({ ...previous, visualTreatment: nextVisual, borderStyle: normalizedBorder, pending: true });
    let response;
    try {
      response = moderatorReset
        ? await choreographyApi.resetCommunityContributionPresentation(orgId, target.id, {
          type: target.type,
          expectedVersion: target.version,
          visualTreatment: 'professional',
          borderStyle: 'clean',
          reason: 'Moderator reset decorative contribution border',
        })
        : await choreographyApi.updateOwnedReviewContribution(target.type, target.id, {
          expectedVersion: target.version,
          changes: { visualTreatment: nextVisual, borderStyle: normalizedBorder },
        });
    } catch {
      setFormatting({ ...previous, pending: false });
      setMutationNotice(`Could not ${moderatorReset ? 'reset' : 'update'} this ${isMedia ? 'media' : 'comment'} frame. Reload and try again.`);
      return;
    }
    const payload = responsePayload(response);
    const nextVersion = Number(payload?.contribution?.version || payload?.version);
    setFormatting({
      visualTreatment: nextVisual,
      borderStyle: normalizedBorder,
      version: Number.isInteger(nextVersion) && nextVersion > target.version ? nextVersion : target.version + 1,
      pending: false,
    });
    try { await onRefresh(); }
    catch { setMutationNotice(`${moderatorReset ? 'Frame reset' : 'Frame updated'}. Reload to refresh the latest version.`); }
  };
  const moveAnchor = async (delta) => {
    const anchor = moveAnchorToAdjacentLine(source, thread.anchor, delta);
    await choreographyApi.moveCommunityAnchor(orgId, thread.threadId, { anchor, expectedVersion: anchorVersion });
    onRefresh();
  };

  const actions = [
    ...(canManageThreadState ? [{ label: thread.state === 'resolved' ? 'Reopen' : 'Resolve', run: changeState }] : []),
    ...(authority.canModerate ? [{ label: 'Approve', run: () => moderate('approved') }, { label: 'Hide contribution', run: () => moderate('hidden') }] : []),
  ];
  const anchorLabel = `Attached to line ${thread.anchor?.sourceLine || 1}${thread.anchor?.exactQuote ? `: ${thread.anchor.exactQuote}` : ''}`;

  // D003 §4: `tone=playful` renders as the decorative ✦ with the accessible
  // text label “Creative comment”; legacy stored tones stay readable.
  const accessibleLabel = `${thread.tone === 'playful' ? 'Creative' : (thread.tone || 'Serious')} comment${authorName ? ` by ${authorName}` : ''}`;
  const reactionLabels = { agree: 'Agree', love: 'Love', try_this: 'Try this', funny: 'Laugh' };
  const formatterTarget = formatTarget === 'media' && canSelectMediaPresentation
    ? { type: 'media', id: media.mediaId, label: media.kind === 'photo' ? 'photo' : 'video', mode: canFormatMedia ? 'owner' : 'moderator' }
    : canSelectCommentPresentation ? { type: 'comment', id: contributionId, label: 'comment', mode: canManageOwnContribution ? 'owner' : 'moderator' } : null;
  const selectedFormatting = formatterTarget?.type === 'media' ? mediaFormatting : commentFormatting;
  const reactions = !readOnly && <div className="mc-review-reactions" aria-label="Reactions">{Object.entries(reactionLabels).map(([reaction, label]) => <button type="button" key={reaction} onClick={() => react(reaction)} disabled={!authority.canContribute} aria-label={`React ${label}`}><span>{label}</span>{thread.reactionCounts?.[reaction] ? ` ${thread.reactionCounts[reaction]}` : ''}</button>)}</div>;

  const contribution = <article className={`mc-review-balloon ${inline ? 'mc-review-inline-bubble' : ''} ${active ? 'active' : ''} ${thread.tone || 'serious'} treatment-${commentFormatting.visualTreatment} border-${commentFormatting.borderStyle}`} aria-label={accessibleLabel} tabIndex="0" data-thread-id={thread.threadId}>
    {!inline && <span className="mc-review-tether" aria-hidden="true"><i /></span>}
    <header><span aria-hidden="true">{thread.tone === 'playful' ? '✦' : '◆'}</span>{authorName && <strong>{authorName}</strong>}{!inline && <small>{thread.state || 'open'}</small>}<ThreadActions actions={actions} authorName={authorName} /></header>
    {!inline && <button type="button" className="mc-review-anchor-chip" onClick={() => onRevealAnchor?.(thread)} aria-label={`Show ${anchorLabel.toLowerCase()} in source`}><span aria-hidden="true">↖</span><span>{anchorLabel}</span></button>}
    {thread.anchor?.state === 'needs_reanchor' && <p className="mc-anchor-warning">Needs re-anchor: “{thread.anchor.exactQuote}”</p>}
    {editing ? <form className="mc-review-edit" aria-label="Edit my review" onSubmit={saveOwnContribution}>
      <label>Edit my review<textarea aria-label="Review text" value={editedBody} onChange={(event) => setEditedBody(event.target.value)} /></label>
      <div><button type="button" onClick={() => { setEditedBody(thread.body || ''); setEditing(false); }}>Cancel</button><button type="submit" disabled={!editedBody.trim()}>Save review</button></div>
    </form> : <p
      className={canSelectCommentPresentation && inline ? 'mc-format-selectable' : undefined}
      role={canSelectCommentPresentation && inline ? 'button' : undefined}
      tabIndex={canSelectCommentPresentation && inline ? 0 : undefined}
      aria-label={canSelectCommentPresentation && inline ? `Select comment for ${canManageOwnContribution ? 'formatting' : 'frame reset'}` : undefined}
      aria-pressed={canSelectCommentPresentation && inline ? formatTarget === 'comment' : undefined}
      onClick={canSelectCommentPresentation && inline ? () => setFormatTarget('comment') : undefined}
      onFocus={canSelectCommentPresentation && inline ? () => setFormatTarget('comment') : undefined}
      onKeyDown={canSelectCommentPresentation && inline ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setFormatTarget('comment'); } } : undefined}
    >{thread.body}</p>}
    {mutationNotice && <p className="mc-review-notice" role="alert">{mutationNotice}</p>}
    {(thread.replies || []).length > 0 && <ol className="mc-review-replies" aria-label="Replies">{thread.replies.map((item) => { const replyAuthor = item.author?.displayName || item.authorName || ''; return <li key={item.replyId || item.commentId}>{replyAuthor && <><strong>{replyAuthor}</strong>{' '}</>}{item.body}<OwnedContributionControls item={item} type="comment" id={item.commentId || item.replyId} value={item.body} label="Reply" changes={(next) => ({ body: next, tone: item.tone || 'serious' })} onRefresh={onRefresh} /></li>; })}</ol>}
    {media && <button type="button" className={`mc-review-media treatment-${mediaFormatting.visualTreatment} border-${mediaFormatting.borderStyle}`} onClick={() => { if (canSelectMediaPresentation && inline) setFormatTarget('media'); setExpanded((value) => !value); }} onFocus={() => { if (canSelectMediaPresentation && inline) setFormatTarget('media'); }} aria-pressed={canSelectMediaPresentation && inline ? formatTarget === 'media' : undefined} aria-expanded={expanded} aria-label={`Open ${media.kind || 'media'} review: ${media.altText || media.caption || 'attachment'}`}>
      {media.kind === 'photo' && photoUrl ? <img src={photoUrl} alt={media.altText || media.caption || ''} /> : <span aria-hidden="true">{media.kind === 'video' ? '▶' : '▣'}</span>}
      <small>{media.caption || media.altText}{media.processingState ? ` · ${media.processingState}` : ''}</small>
    </button>}
    {media && <OwnedContributionControls item={media} type="media" id={media.mediaId} value={media.caption || media.altText} label="Media" changes={(next) => ({ caption: next, altText: media.altText || next })} onRefresh={onRefresh} />}
    {expanded && media && <section className="mc-frame-review" aria-label="Frame annotation tools">
      <div className="mc-frame-stage" aria-label={media.altText || media.caption || 'Paused media frame'}>{!readOnly && media.posterUrl && <img src={media.posterUrl} alt="" />}{(media.shapes || []).map((shape) => <span key={shape.shapeId} className={`mc-frame-shape ${shape.kind}`}>{shape.label}</span>)}</div>
      {canAnnotateMedia && <><label>Shape description<input aria-label="Shape description" value={shapeLabel} onChange={(event) => setShapeLabel(event.target.value)} /></label><div className="mc-shape-tools">{FRAME_SHAPE_TOOLS.map((tool) => <button key={tool.id} type="button" onClick={() => addShape(tool)} disabled={!shapeLabel.trim()}>{tool.label}</button>)}</div></>}
      <ul aria-label="Frame shape descriptions">{(media.shapes || []).map((shape) => <li key={shape.shapeId}>{shape.kind}: {shape.label}<OwnedContributionControls item={shape} type="annotation" id={shape.shapeId} value={shape.label} label="Annotation" changes={(next) => ({ label: next })} onRefresh={onRefresh} /></li>)}</ul>
    </section>}
    {!inline && reactions}
    {canMoveOwnAnchor && !inline && <div className="mc-move-anchor" aria-label="Move anchor"><button type="button" onClick={() => moveAnchor(-1)}>Previous line</button><button type="button" onClick={() => moveAnchor(1)}>Next line</button></div>}
    {canManageOwnContribution && !editing && <div className="mc-review-own-actions" aria-label="My review actions"><button type="button" className="mc-review-icon-action" aria-label="Edit my review" title="Edit" onClick={() => setEditing(true)}><span aria-hidden="true">✎</span></button><button type="button" className="mc-review-icon-action" aria-label="Withdraw my review" title="Withdraw" onClick={withdrawOwnContribution}><span aria-hidden="true">×</span></button></div>}
    {authority.canContribute && <form onSubmit={postReply} className="mc-review-reply"><label><span className="mc-visually-hidden">Reply</span><input aria-label="Reply" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Reply" /></label><button type="submit" disabled={!reply.trim()}>Send</button></form>}
  </article>;

  if (!inline || !active) return contribution;
  return <div className="mc-review-active-stack">
    {formatterTarget && <ContributionFormatter target={formatterTarget} formatting={selectedFormatting} onApply={applyBorder} />}
    <div className="mc-review-contribution-row">
      {contribution}
      {tryThisFlourish && <div className={`mc-try-this-flourish${reducedMotion ? ' reduced-motion' : ''}`} role="status" aria-label="Try this acknowledged" data-motion={reducedMotion ? 'static' : 'drawn'}>
        <svg viewBox="0 0 64 96" aria-hidden="true">
          <circle className="mc-stick-head" cx="32" cy="16" r="9" />
          <path className="mc-stick-line body" d="M32 25 L32 56" />
          <path className="mc-stick-line arms" d="M32 35 L12 24 M32 35 L53 20" />
          <path className="mc-stick-line legs" d="M32 56 L16 82 M32 56 L51 79" />
          <path className="mc-stick-spark" d="M51 8 L54 14 L61 15 L56 20 L57 27 L51 23 L45 27 L46 20 L41 15 L48 14 Z" />
        </svg>
      </div>}
    </div>
    {reactions && <div className="mc-reaction-bubble">{reactions}</div>}
  </div>;
}

export function CommunityReviewLayer({ orgId, routineId, revisionId, draftId, draftServerSequence, source, sourceRef, capabilities = {}, anonymous = false, loadCommunity, readOnly = false, display = 'inline', selectionActions = [], sections = [], commentsSuppressed = false }) {
  const [community, setCommunity] = useState({ policy: { mode: 'view', contributionsEnabled: false }, authority: {}, threads: [], media: [] });
  const [composer, setComposer] = useState(null);
  const [selectionPrompt, setSelectionPrompt] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [sourceEndpoint, setSourceEndpoint] = useState(null);
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const [altText, setAltText] = useState('');
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [videoDurationSeconds, setVideoDurationSeconds] = useState('');
  const [visualTreatment, setVisualTreatment] = useState('professional');
  const [borderStyle, setBorderStyle] = useState('clean');
  const [sectionTag, setSectionTag] = useState('');
  const [tagStartCount, setTagStartCount] = useState('');
  const [tagEndCount, setTagEndCount] = useState('');
  const [filter, setFilter] = useState('all');
  const [notice, setNotice] = useState('');
  const [posting, setPosting] = useState(false);
  const [postPhase, setPostPhase] = useState('draft');
  const [, setInlinePositionRevision] = useState(0);
  const selectionToolbarRef = useRef(null);
  const suppressCaptureRef = useRef(false);
  const postRequestRef = useRef(null);
  const postedReceiptRef = useRef(null);
  const activeRevisionId = revisionId || community.revisionId;
  // MCRO001-D003 §4/§5 owner-approved amendment: absent a published binding,
  // fall back to the current live draft — from the explicit prop (the Canvas
  // editor's own F0024 collaboration document) or, failing that, the
  // server's own resolved draft binding from getCommunity.
  const activeDraftId = !activeRevisionId ? (draftId || community.draftId) : null;
  const activeDraftServerSequence = !activeRevisionId
    ? (draftId ? draftServerSequence : community.draftServerSequence) : null;
  const activeBinding = activeRevisionId || (activeDraftId
    ? { draftId: activeDraftId, draftServerSequence: activeDraftServerSequence } : null);
  // Repositioning memory is keyed by routine, revision/draft, surface type
  // and thread/draft (D003 §4): a draft binding gets its own stable key so
  // switching between a published revision and its live draft never reuses
  // stale floating-surface position memory.
  const activeBindingKey = activeRevisionId ? `rev:${activeRevisionId}` : activeDraftId ? `draft:${activeDraftId}` : 'none';
  // D003 §4: one canonical newline-normalised anchorSource and offset grammar
  // shared with the backend validator (UTF-16 code units, CRLF/CR → LF).
  const anchorSource = useMemo(() => String(source || '').replace(/\r\n?/g, '\n'), [source]);

  const load = useCallback(async () => {
    if (!routineId || (!loadCommunity && !orgId)) return;
    try {
      const response = loadCommunity
        ? await loadCommunity(routineId)
        : await choreographyApi.getCommunity(orgId, routineId);
      setCommunity((current) => ({ ...current, ...normalizeCommunity(responsePayload(response)) }));
      setNotice('');
      return true;
    }
    catch (error) { setNotice(errorMessage(error)); return false; }
  }, [loadCommunity, orgId, routineId]);
  useEffect(() => { load(); }, [load]);
  const previousDisplayRef = useRef(display);
  useEffect(() => {
    if (previousDisplayRef.current === 'hide' && display !== 'hide') load();
    previousDisplayRef.current = display;
  }, [display, load]);
  useEffect(() => {
    if (display !== 'inline') return undefined;
    const refreshPositions = () => setInlinePositionRevision((value) => value + 1);
    const target = sourceRef?.current;
    globalThis.addEventListener?.('resize', refreshPositions);
    globalThis.addEventListener?.('scroll', refreshPositions, true);
    target?.addEventListener?.('scroll', refreshPositions);
    return () => {
      globalThis.removeEventListener?.('resize', refreshPositions);
      globalThis.removeEventListener?.('scroll', refreshPositions, true);
      target?.removeEventListener?.('scroll', refreshPositions);
    };
  }, [display, sourceRef]);
  useEffect(() => {
    if (display !== 'inline' || !activeThreadId) return undefined;
    const dismiss = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'keydown' && (event.defaultPrevented || event.target?.closest?.('[role="menu"]'))) return;
      if (event.type === 'pointerdown' && event.target?.closest?.('.mc-inline-review-marker')) return;
      const dismissedThreadId = activeThreadId;
      setActiveThreadId(null);
      sourceRef?.current?.dispatchEvent?.(new CustomEvent('studioaeye:review-selection'));
      if (event.type === 'keydown') globalThis.setTimeout?.(() => {
        globalThis.document?.querySelector(`.mc-inline-review-marker[data-thread-id="${String(dismissedThreadId)}"] .mc-inline-anchor-diamond`)?.focus?.();
      }, 0);
    };
    globalThis.document?.addEventListener('keydown', dismiss);
    globalThis.document?.addEventListener('pointerdown', dismiss);
    return () => {
      globalThis.document?.removeEventListener('keydown', dismiss);
      globalThis.document?.removeEventListener('pointerdown', dismiss);
    };
  }, [activeThreadId, display, sourceRef]);

  const authority = useMemo(() => effectiveCommunityAuthority(capabilities, community.policy, community.authority, anonymous || readOnly), [anonymous, capabilities, community.authority, community.policy, readOnly]);
  // D003 §7: Hide removes the contextual toolbar, comment shortcut and review
  // presentation. D003 §4/§5 owner-approved amendment: no comment affordance
  // without contribution authority, but a published revision is no longer
  // required — a published revision OR the routine's current live draft
  // binding is sufficient.
  const canOfferComment = authority.canContribute && !readOnly && display !== 'hide' && Boolean(activeBinding);
  const openComposer = useCallback((capturedAnchor = null) => {
    if (!authority.canContribute || display === 'hide' || !activeBinding) return;
    const target = sourceRef?.current;
    const anchor = capturedAnchor?.type
      ? capturedAnchor
      : anchorFromSelection(anchorSource, target?.selectionStart, target?.selectionEnd, activeBinding, community.sourceHash);
    setComposer(anchor);
    setSelectionPrompt(null);
    setPostPhase('draft');
    postRequestRef.current = null;
    postedReceiptRef.current = null;
    setBody(''); setFile(null); setAltText(''); setRightsConfirmed(false); setConsentConfirmed(false); setVideoDurationSeconds('');
    setSectionTag(''); setTagStartCount(''); setTagEndCount('');
  }, [activeBinding, authority.canContribute, community.sourceHash, display, anchorSource, sourceRef]);
  const openComposerRef = useRef(openComposer);
  const selectionPromptRef = useRef(null);
  useEffect(() => { selectionPromptRef.current = selectionPrompt; }, [selectionPrompt]);
  // MCRO001-F0060/T-MCRO-060: a ref (not the raw prop) so the native
  // listeners below — registered once per `active`/binding change, not on
  // every hover/focus tick of the rail — always read the CURRENT suppression
  // state without needing to be torn down and re-attached on each toggle.
  const commentsSuppressedRef = useRef(commentsSuppressed);
  useEffect(() => { commentsSuppressedRef.current = commentsSuppressed; }, [commentsSuppressed]);
  // Owner ruling (2026-08-26): entering rail edit-mode must close an
  // already-open comment prompt immediately, not merely block new ones.
  useEffect(() => { if (commentsSuppressed) setSelectionPrompt(null); }, [commentsSuppressed]);
  // Keep the document shortcut on the same render as the visible review state.
  // A passive ref update leaves a small window where the loaded thread is
  // visible but Ctrl/Meta+Alt+M still calls the initial denied-authority
  // callback. Updating before paint closes that user-visible timing gap.
  useLayoutEffect(() => { openComposerRef.current = openComposer; }, [openComposer]);
  useEffect(() => {
    const shortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.altKey && String(event.key).toLowerCase() === 'm') {
        event.preventDefault();
        // MCRO001-F0060: the keyboard fallback is a comment-opening action
        // too, so it is withheld for the same duration as the pointer-driven
        // toolbar while a demonstration timing rail is in edit mode (see the
        // other commentsSuppressedRef check below for why the dataset read
        // is also here).
        if (commentsSuppressedRef.current || globalThis.document?.body?.dataset?.mcRailEditModeActive === '1') return;
        // D003 §4: the keyboard fallback opens the composer for the captured
        // range when one exists, exactly like activating the toolbar.
        openComposerRef.current(selectionPromptRef.current?.anchor || null);
      }
    };
    globalThis.document?.addEventListener('keydown', shortcut);
    return () => globalThis.document?.removeEventListener('keydown', shortcut);
  }, []);
  const hasStructuralActions = selectionActions.length > 0;
  useEffect(() => {
    const target = sourceRef?.current;
    const active = canOfferComment || (hasStructuralActions && display !== 'hide');
    if (!target?.addEventListener || !active) {
      setSelectionPrompt(null);
      return undefined;
    }
    const updateSelection = () => {
      // Escape restores the selection programmatically; browsers re-fire a
      // `select` event for that restore, which must not reopen the toolbar.
      if (suppressCaptureRef.current) {
        suppressCaptureRef.current = false;
        return;
      }
      // MCRO001-F0060/T-MCRO-060 (owner ruling 2026-08-26): while a
      // demonstration timing rail is hovered/focused/being dragged, the
      // comment box must be UNABLE to open at all — not merely have its
      // triggering event's propagation stopped (the rail and this text
      // column are DOM siblings, not ancestor/descendant, so a rail gesture
      // was never guaranteed to reach here via bubbling anyway; the real
      // leak was a native browser text-selection landing on this textarea
      // whenever a hover-expanded rail's drag path crosses real text pixels
      // — confirmed via independent real-browser testing to happen even
      // while the rail's own pointer events are correctly captured, so
      // `user-select: none` cannot prevent it (that CSS property does not
      // govern a textarea's internal selection in any browser).
      //
      // `commentsSuppressedRef` is the React-idiomatic, unit-tested contract
      // (prop -> ref, updated in an effect). `document.body.dataset` is a
      // SYNCHRONOUS, zero-render-latency read of the SAME signal —
      // DemonstrationTimingRail.js sets it directly, with no React render
      // cycle in between. That gap matters here specifically: a native
      // selection event can fire before `commentsSuppressed` has finished
      // propagating through two components' worth of re-renders (this one
      // is a sibling of the rail, not its parent), especially right at drag
      // start — the dataset read closes exactly that window.
      if (commentsSuppressedRef.current || globalThis.document?.body?.dataset?.mcRailEditModeActive === '1') {
        // Collapsing here, not merely ignoring this event, is what actually
        // closes the race: a stray selection that forms mid-drag would
        // otherwise sit on the textarea, non-collapsed, until AFTER
        // suppression lifts — at which point some unrelated LATER event
        // (blur, a keyup, focus returning) can rediscover that stale
        // selection and open the toolbar even though the drag is long over.
        if (target.selectionStart !== target.selectionEnd) {
          suppressCaptureRef.current = true;
          target.setSelectionRange(target.selectionStart, target.selectionStart);
        }
        return;
      }
      const start = Number(target.selectionStart);
      const end = Number(target.selectionEnd);
      if (!Number.isInteger(start) || !Number.isInteger(end) || start === end) {
        setSelectionPrompt(null);
        return;
      }
      // The anchor is captured from the live selection BEFORE toolbar focus
      // can collapse it; activating any toolbar action uses this capture.
      const anchor = anchorFromSelection(anchorSource, start, end, activeBinding, community.sourceHash);
      setSelectionPrompt({ anchor, position: selectionToolbarPosition(target, anchorSource, end) });
    };
    target.addEventListener('select', updateSelection);
    target.addEventListener('mouseup', updateSelection);
    target.addEventListener('pointerup', updateSelection);
    target.addEventListener('keyup', updateSelection);
    target.addEventListener('touchend', updateSelection);
    return () => {
      target.removeEventListener('select', updateSelection);
      target.removeEventListener('mouseup', updateSelection);
      target.removeEventListener('pointerup', updateSelection);
      target.removeEventListener('keyup', updateSelection);
      target.removeEventListener('touchend', updateSelection);
    };
  }, [activeBinding, canOfferComment, community.sourceHash, display, hasStructuralActions, anchorSource, sourceRef]);
  useEffect(() => {
    if (!selectionPrompt) return undefined;
    const dismiss = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      const target = sourceRef?.current;
      suppressCaptureRef.current = true;
      target?.focus?.();
      target?.setSelectionRange?.(selectionPrompt.anchor.sourceStart, selectionPrompt.anchor.sourceEnd);
      setSelectionPrompt(null);
    };
    const clickAway = (event) => {
      if (selectionToolbarRef.current?.contains(event.target) || event.target === sourceRef?.current) return;
      setSelectionPrompt(null);
    };
    globalThis.document?.addEventListener('keydown', dismiss);
    globalThis.document?.addEventListener('pointerdown', clickAway);
    return () => {
      globalThis.document?.removeEventListener('keydown', dismiss);
      globalThis.document?.removeEventListener('pointerdown', clickAway);
    };
  }, [selectionPrompt, sourceRef]);

  // D003 §4 / T-MCRO-015: after an accepted receipt the composer closes only
  // when a reload of authorised community state contains the accepted thread
  // at the same exact anchor. Accepted-then-refresh-failed retries GET only.
  const verifyPostedThread = useCallback(async () => {
    const receipt = postedReceiptRef.current;
    if (!receipt) return false;
    let next;
    try {
      const response = loadCommunity
        ? await loadCommunity(routineId)
        : await choreographyApi.getCommunity(orgId, routineId);
      next = normalizeCommunity(responsePayload(response));
    } catch {
      setPostPhase('refresh_failed');
      setNotice('Comment posted; refresh failed.');
      return false;
    }
    setCommunity((current) => ({ ...current, ...next }));
    const acceptedThreadId = receipt.threadId || receipt.thread?.threadId;
    const posted = (next.threads || []).find((thread) => String(thread.threadId) === String(acceptedThreadId));
    const anchorMatches = !posted?.anchor
      || ((Number(posted.anchor.sourceStart) === Number(receipt.anchor?.sourceStart ?? posted.anchor.sourceStart))
        && (Number(posted.anchor.sourceEnd) === Number(receipt.anchor?.sourceEnd ?? posted.anchor.sourceEnd)));
    if (posted && anchorMatches) {
      setComposer(null);
      setPostPhase('draft');
      postRequestRef.current = null;
      postedReceiptRef.current = null;
      setNotice('');
      return true;
    }
    setPostPhase('refresh_failed');
    setNotice('Comment posted; refresh failed.');
    return false;
  }, [loadCommunity, orgId, routineId]);

  const post = async (event) => {
    event.preventDefault();
    if (!body.trim() || !composer || posting || postPhase === 'refresh_failed') return;
    setPosting(true); setNotice('');
    try {
      let mediaId;
      if (file) {
        const form = new FormData();
        form.append('file', file); form.append('revisionId', revisionId || community.revisionId || ''); form.append('caption', body.trim()); form.append('altText', altText.trim()); form.append('rightsConfirmed', String(rightsConfirmed)); form.append('consentConfirmed', String(consentConfirmed)); form.append('anchor', JSON.stringify(composer)); form.append('visualTreatment', visualTreatment); form.append('borderStyle', borderStyle);
        if (file.type.startsWith('video/')) form.append('durationMs', String(Math.round(Number(videoDurationSeconds) * 1000)));
        let uploaded;
        try {
          uploaded = responsePayload(await choreographyApi.uploadCommunityMedia(orgId, routineId, form));
        } catch (uploadError) {
          // M004-F0664 (#117): an upload failure is about the ATTACHMENT. A 404
          // here means the upload route is absent, not that the reviewer lost
          // access, so the generic "review not available" copy would mislead.
          const missing = Number(uploadError?.response?.status) === 404;
          setNotice(missing
            ? 'Attachments cannot be uploaded right now, so your comment was not posted. Remove the attachment to post the comment without it.'
            : `The attachment could not be uploaded, so your comment was not posted. ${errorMessage(uploadError)}`);
          return;
        }
        mediaId = uploaded.mediaId || uploaded.media?.mediaId;
      }
      // Optional section/count tag becomes anchor evidence the backend
      // validates against the same published revision (D003 §4/§5).
      const tagStart = Number(tagStartCount);
      const tagEnd = Number(tagEndCount);
      const anchor = {
        ...composer,
        ...(sectionTag ? { sectionId: sectionTag } : {}),
        ...(Number.isInteger(tagStart) && tagStart >= 1 && Number.isInteger(tagEnd) && tagEnd >= tagStart
          ? { startCount: tagStart, endCount: tagEnd } : {}),
      };
      // The basic composer exposes no tone choice: new comments send the
      // existing `tone=playful` value (D003 §4). MCRO001-D003 §4/§5
      // owner-approved amendment: post whichever exact binding was captured
      // with the anchor — a published revisionId, or a draft's
      // draftId/draftServerSequence — never both.
      const request = {
        ...(anchor.draftId
          ? { draftId: anchor.draftId, draftServerSequence: anchor.draftServerSequence }
          : { revisionId: revisionId || community.revisionId }),
        anchor, body: body.trim(), tone: 'playful', mediaId, visualTreatment, borderStyle,
      };
      // One idempotency key per exact payload: exact and ambiguous-network
      // retries reuse it; editing the payload starts a new attempt/key.
      const fingerprint = JSON.stringify([request.revisionId || null, request.draftId || null, request.draftServerSequence ?? null, anchor, request.body, mediaId || null]);
      if (!postRequestRef.current || postRequestRef.current.fingerprint !== fingerprint) {
        postRequestRef.current = { clientRequestId: newClientRequestId(), fingerprint };
      }
      if (postRequestRef.current.clientRequestId) request.clientRequestId = postRequestRef.current.clientRequestId;
      const receipt = responsePayload(await choreographyApi.createCommunityThread(orgId, routineId, request));
      postedReceiptRef.current = { ...receipt, anchor: receipt.anchor || anchor };
      await verifyPostedThread();
    } catch (error) {
      // A definitive denial/stale/validation failure keeps the draft and
      // anchor for correction; a 5xx or network failure retains the same
      // request key for a safe retry. No failure claims success.
      setNotice(errorMessage(error));
    }
    finally { setPosting(false); }
  };
  const threadsWithLines = (community.threads || []).map((thread) => ({
    ...thread,
    anchor: thread.anchor ? { ...thread.anchor, sourceLine: thread.anchor.sourceLine || lineForOffset(source, thread.anchor.sourceStart) } : thread.anchor,
  }));
  const visibleThreads = threadsWithLines.filter((thread) => {
    if (filter === 'all') return true;
    if (filter === 'media') return Boolean(thread.media || thread.attachments?.length);
    if (filter === 'unread') return thread.unread === true;
    if (filter === 'mine') return thread.ownedByViewer === true;
    return thread.state === filter;
  });
  const byAnchor = (a, b) => Number(a.anchor?.sourceLine || 0) - Number(b.anchor?.sourceLine || 0) || String(a.threadId).localeCompare(String(b.threadId));
  const packed = [...visibleThreads].sort(byAnchor);
  const inlinePacked = [...threadsWithLines].sort(byAnchor);
  if (!authority.canView) return null;
  const policyMode = community.policy?.mode || 'view';
  // A Review grant may sit below an organisation's Edit ceiling. Do not label
  // that reviewer as an editor or surface policy/access elevation controls.
  const displayMode = readOnly ? 'view' : (policyMode === 'edit' && !authority.canEdit ? (authority.canContribute ? 'comment' : 'view') : policyMode);
  const filters = readOnly ? ['open', 'resolved', 'media', 'all'] : ['open', 'unread', 'mine', 'resolved', 'media', 'all'];
  const selectedThreads = selectionPrompt ? threadsWithLines.filter((thread) => {
    const start = Number(thread.anchor?.sourceStart);
    const end = Number(thread.anchor?.sourceEnd);
    return Number.isInteger(start) && Number.isInteger(end)
      && start < selectionPrompt.anchor.sourceEnd && end > selectionPrompt.anchor.sourceStart;
  }) : [];
  const focusThread = (threadId) => {
    const thread = threadsWithLines.find((item) => String(item.threadId) === String(threadId));
    if (display === 'inline' && thread) activateInlineThread(thread);
    else setActiveThreadId(threadId);
    globalThis.setTimeout?.(() => {
      const card = [...(globalThis.document?.querySelectorAll('.mc-review-balloon[data-thread-id]') || [])].find((item) => item.dataset.threadId === String(threadId));
      card?.focus?.();
      card?.scrollIntoView?.({ block: 'nearest' });
    }, 0);
    setSelectionPrompt(null);
  };
  const revealAnchor = (thread) => {
    const target = sourceRef?.current;
    if (!target?.setSelectionRange || !thread.anchor) return;
    const start = Math.max(0, Number(thread.anchor.sourceStart) || 0);
    const end = Math.max(start, Number(thread.anchor.sourceEnd) || start);
    const lineHeight = Number.parseFloat(globalThis.getComputedStyle?.(target)?.lineHeight) || 24;
    target.focus?.({ preventScroll: true });
    target.setSelectionRange(start, end);
    target.scrollTop = Math.max(0, ((Number(thread.anchor.sourceLine) || lineForOffset(source, start)) - 2) * lineHeight);
    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    target.scrollIntoView?.({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    const position = selectionToolbarPosition(target, source, end);
    setActiveThreadId(thread.threadId);
    setSourceEndpoint({ top: position.top, left: `${Math.round((target.getBoundingClientRect?.().right || 0) - 8)}px` });
    if (authority.canContribute && !readOnly && activeBinding && start !== end) {
      setSelectionPrompt({ anchor: anchorFromSelection(source, start, end, activeBinding, community.sourceHash), position });
    }
  };
  const inlineAnchorRange = (thread) => {
    const requestedStart = Number(thread.anchor?.sourceStart);
    const line = Math.max(1, Number(thread.anchor?.sourceLine) || 1);
    const lineStart = String(source || '').split('\n').slice(0, line - 1).reduce((offset, item) => offset + item.length + 1, 0);
    const start = Number.isInteger(requestedStart) ? Math.max(0, requestedStart) : lineStart;
    const requestedEnd = Number(thread.anchor?.sourceEnd);
    const quoteLength = String(thread.anchor?.exactQuote || '').length;
    const end = Number.isInteger(requestedEnd) && requestedEnd > start ? requestedEnd : start + quoteLength;
    return { start, end: Math.max(start, end) };
  };
  const activateInlineThread = (thread) => {
    const target = sourceRef?.current;
    const range = inlineAnchorRange(thread);
    target?.setSelectionRange?.(range.start, range.end);
    if (target?.dispatchEvent && typeof CustomEvent === 'function') {
      target.dispatchEvent(new CustomEvent('studioaeye:review-selection', { detail: range }));
    }
    setSelectionPrompt(null);
    setActiveThreadId(thread.threadId);
  };
  const deactivateInlineThread = (event, thread) => {
    const contains = (candidate) => candidate && typeof candidate.nodeType === 'number' && event.currentTarget.contains(candidate);
    if (contains(event.relatedTarget) || contains(globalThis.document?.activeElement)) return;
    if (activeThreadId !== thread.threadId) return;
    setActiveThreadId(null);
    const target = sourceRef?.current;
    if (target?.dispatchEvent && typeof CustomEvent === 'function') target.dispatchEvent(new CustomEvent('studioaeye:review-selection'));
  };
  if (display === 'hide') return null;
  const renderedDisplay = display === 'inline' && !sourceRef ? 'list' : display;
  const renderedThreads = renderedDisplay === 'inline' ? inlinePacked : packed;
  const emptyCopy = filter === 'open' || filter === 'all' ? 'No notes yet. Highlight a movement to start the conversation.' : `No ${filter} notes yet.`;

  return <>{sourceEndpoint && <span className="mc-review-source-endpoint" style={sourceEndpoint} aria-hidden="true" />}{selectionPrompt && <SelectionToolbar
    toolbarRef={selectionToolbarRef}
    position={selectionPrompt.position}
    quote={selectionPrompt.anchor.exactQuote}
    onDismiss={() => { sourceRef?.current?.focus?.(); setSelectionPrompt(null); }}
  >
    {canOfferComment && <button type="button" className="mc-selection-comment" onClick={() => openComposer(selectionPrompt.anchor)}>Add comment</button>}
    {selectionActions.map((action) => <button
      type="button"
      key={action.id}
      className={`mc-selection-structural${action.disabled ? ' unavailable' : ''}`}
      disabled={action.disabled === true}
      title={action.description}
      onClick={() => {
        const anchor = selectionPrompt.anchor;
        setSelectionPrompt(null);
        action.run({ sourceStart: anchor.sourceStart, sourceEnd: anchor.sourceEnd, exactQuote: anchor.exactQuote });
      }}
    >{action.label}</button>)}
    {canOfferComment && selectedThreads.length > 0 && <button type="button" onClick={() => focusThread(selectedThreads[0].threadId)}>View {selectedThreads.length} {selectedThreads.length === 1 ? 'note' : 'notes'}</button>}
  </SelectionToolbar>}
  <aside className={`mc-community-review mc-review-${renderedDisplay}`} aria-labelledby={renderedDisplay === 'list' ? 'mc-review-title' : undefined} aria-label={renderedDisplay === 'inline' ? 'Inline creative notes' : undefined}>
    {renderedDisplay === 'list' && <><header className="mc-review-toolbar"><div><span className="mc-kicker">Creative notes</span><div className="mc-review-title-row"><h2 id="mc-review-title">Review</h2><span className="mc-review-mode-badge">{COMMUNITY_MODES.find(({ id }) => id === displayMode)?.label || 'View'}</span></div></div>{!readOnly && <button type="button" className="mc-review-add" onClick={() => openComposer()} disabled={!authority.canContribute || !activeBinding} aria-keyshortcuts="Control+Alt+M Meta+Alt+M">+ Comment</button>}</header>
    <p className="mc-review-mode">A focused list for reading every note, reaction and reply.</p>
    <nav className="mc-review-filters" aria-label="Filter review contributions">{filters.map((item) => <button type="button" key={item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)} aria-pressed={filter === item}>{item}</button>)}</nav></>}
    {notice && <p className="mc-review-notice" role="status">{notice}</p>}
    {composer && (() => {
      const composerForm = <form className="mc-review-composer" onSubmit={post} aria-label="Add anchored comment">
        <p className="mc-composer-tone"><span aria-hidden="true">✦</span><span>Creative comment</span></p>
        {renderedDisplay === 'list' && <p>Anchored to line {composer.sourceLine}{composer.exactQuote ? `: “${composer.exactQuote}”` : ''}</p>}
        <label>Comment<textarea autoFocus aria-label="Comment" value={body} onChange={(event) => setBody(event.target.value)} required disabled={postPhase === 'refresh_failed'} /></label>
        <div className="mc-review-tags">
          {sections.length > 0 && <label>Section tag<select aria-label="Section tag" value={sectionTag} onChange={(event) => setSectionTag(event.target.value)} disabled={postPhase === 'refresh_failed'}><option value="">No section tag</option>{sections.map((section) => <option key={section.id} value={section.id}>{section.label}</option>)}</select></label>}
          <label>Count tag from<input aria-label="Count tag from" type="number" min="1" value={tagStartCount} onChange={(event) => setTagStartCount(event.target.value)} disabled={postPhase === 'refresh_failed'} /></label>
          <label>Count tag to<input aria-label="Count tag to" type="number" min="1" value={tagEndCount} onChange={(event) => setTagEndCount(event.target.value)} disabled={postPhase === 'refresh_failed'} /></label>
        </div>
        {authority.canUploadMedia && <div className="mc-review-upload"><label>Photo or video<input aria-label="Photo or video" type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" onChange={(event) => { setFile(event.target.files?.[0] || null); setVideoDurationSeconds(''); }} /></label>{file && <><label>Caption / alt description<input aria-label="Caption or alt description" value={altText} onChange={(event) => setAltText(event.target.value)} required /></label>{file.type.startsWith('video/') && <label>Video duration in seconds<input aria-label="Video duration in seconds" type="number" min="1" max="600" value={videoDurationSeconds} onChange={(event) => setVideoDurationSeconds(event.target.value)} required /></label>}<label><input aria-label="I have the right to share this media" type="checkbox" checked={rightsConfirmed} onChange={(event) => setRightsConfirmed(event.target.checked)} /> I have the right to share this media.</label><label><input aria-label="People shown consent to this use" type="checkbox" checked={consentConfirmed} onChange={(event) => setConsentConfirmed(event.target.checked)} /> People shown consent to this use.</label><label>Presentation<select aria-label="Media presentation" value={visualTreatment} onChange={(event) => setVisualTreatment(event.target.value)}>{COMMUNITY_VISUAL_TREATMENTS.map((item) => <option value={item.id} key={item.id}>{item.label}</option>)}</select></label><label>Decorative border<select aria-label="Decorative border" value={borderStyle} onChange={(event) => setBorderStyle(event.target.value)}>{COMMUNITY_BORDER_STYLES.map((item) => <option value={item.id} key={item.id}>{item.label}</option>)}</select></label></>}</div>}
        {postPhase === 'refresh_failed' && <p className="mc-review-notice" role="alert">Comment posted; refresh failed. <button type="button" onClick={verifyPostedThread}>Retry refresh</button></p>}
        <div><button type="button" onClick={() => { setComposer(null); setPostPhase('draft'); postRequestRef.current = null; postedReceiptRef.current = null; }}>Cancel</button><button type="submit" className="mc-primary" disabled={posting || postPhase === 'refresh_failed' || !body.trim() || (file && (!altText.trim() || !rightsConfirmed || !consentConfirmed || (file.type.startsWith('video/') && !(Number(videoDurationSeconds) >= 1 && Number(videoDurationSeconds) <= 600))))}>{posting ? 'Posting…' : 'Post'}</button></div>
      </form>;
      return renderedDisplay === 'inline'
        ? <FloatingReviewSurface
          surfaceKey={`composer:${routineId}:${activeBindingKey}:${composer.sourceStart}-${composer.sourceEnd}`}
          label="comment composer"
          className="mc-floating-composer"
          style={selectionToolbarPosition(sourceRef?.current, anchorSource, composer.sourceStart)}
        >{composerForm}</FloatingReviewSurface>
        : composerForm;
    })()}
    {renderedDisplay === 'inline' ? <div className="mc-inline-review-markers" aria-label={`${renderedThreads.length} anchored ${renderedThreads.length === 1 ? 'note' : 'notes'}`}>{renderedThreads.map((thread, index) => {
      const range = inlineAnchorRange(thread);
      const sameAnchorIndex = renderedThreads.slice(0, index).filter((candidate) => inlineAnchorRange(candidate).start === range.start).length;
      const sameAnchorCount = renderedThreads.filter((candidate) => inlineAnchorRange(candidate).start === range.start).length;
      const active = activeThreadId === thread.threadId;
      const quote = String(thread.anchor?.exactQuote || '').trim();
      const bubbleId = `mc-review-bubble-${String(thread.threadId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      return <div
        key={thread.threadId}
        className={`mc-inline-review-marker${active ? ' active' : ''}`}
        style={{ ...inlineAnchorPosition(sourceRef?.current, source, range.start), marginLeft: `${sameAnchorIndex * 12}px` }}
        data-thread-id={thread.threadId}
        onMouseEnter={() => activateInlineThread(thread)}
        onMouseLeave={(event) => deactivateInlineThread(event, thread)}
        onFocus={() => activateInlineThread(thread)}
        onBlur={(event) => deactivateInlineThread(event, thread)}
      >
        <button type="button" className="mc-inline-anchor-diamond" aria-label={`Review note${quote ? ` for ${quote}` : ` at line ${thread.anchor?.sourceLine || 1}`}${sameAnchorCount > 1 ? `, ${sameAnchorIndex + 1} of ${sameAnchorCount}` : ''}`} aria-expanded={active} aria-controls={bubbleId} onClick={() => activateInlineThread(thread)}><span aria-hidden="true">◆</span></button>
        {active && <div id={bubbleId}><FloatingReviewSurface surfaceKey={`bubble:${routineId}:${activeBindingKey}:${thread.threadId}`} label="comment bubble" className="mc-floating-bubble"><ThreadCard thread={thread} authority={authority} orgId={orgId} onRefresh={load} source={source} readOnly={readOnly} inline active /></FloatingReviewSurface></div>}
      </div>;
    })}</div> : <div className="mc-review-rail" data-review-layout="natural-tethered">{renderedThreads.length === 0 ? <p className="mc-review-empty">{emptyCopy}</p> : renderedThreads.map((thread) => thread.cluster ? <button type="button" className="mc-review-cluster" key={thread.threadId} onClick={() => setFilter('all')}>{thread.cluster.length} notes · show all</button> : <div key={thread.threadId} className="mc-review-packed"><ThreadCard thread={thread} authority={authority} orgId={orgId} onRefresh={load} source={source} readOnly={readOnly} active={activeThreadId === thread.threadId} onRevealAnchor={revealAnchor} /></div>)}</div>}
  </aside></>;
}
